import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';

import './Interview.css';
import './InterviewPayment.css';
import cardPayment from '../../../Assets/card-payment2.png';
import { COURSES_URL, ENROLLMENT_URL, INTERVIEWMANAGEMENT_URL, JOBS_PORTAL_URL } from '../../../Services/courseService';

const AIInterviewLanding = () => {
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [interviewRemainingAttempts, setInterviewRemainingAttempts] = useState(false);
  const [totalAttempts, setTotalAttempts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoadingInitialCheck, setIsLoadingInitialCheck] = useState(true); // 🔁 Initial load control

  const navigate = useNavigate();
  const { id, courseName } = useParams();

  const fullName = localStorage.getItem('fullName');
  const email = localStorage.getItem('email');
  const phoneNumber = localStorage.getItem('phoneNumber') || '';
  const userId = localStorage.getItem('userId');

  const originalAmount = 99;
  const totalAmount = originalAmount - discountAmount;

  useEffect(() => {
    const checkEnrollmentStatus = async () => {
      try {
        const response = await axios.get(`${ENROLLMENT_URL}/${userId}/${id}/getEnrollmentStatus`);
        if (response.status !== 200) navigate('/');
      } catch {
        navigate('/');
      }
    };
    checkEnrollmentStatus();
  }, [navigate, userId, id]);

  const getInterviewRemainingAttempts = useCallback(async () => {
    try {
      const response = await axios.get(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/remaining-attempts/${userId}/${id}`);
      setInterviewRemainingAttempts(response?.data?.allowed);
      setTotalAttempts(response?.data?.totalAttempts);
      if (response?.data?.remainingAttempts >= 1) {
        toast.success(`You have ${response?.data?.remainingAttempts} attempt(s) left`);
      }
    } catch (error) {
      console.error("Error fetching remaining attempts:", error);
    } finally {
      setIsLoadingInitialCheck(false); // ✅ End loading
    }
  }, [id, userId]);

  useEffect(() => {
    getInterviewRemainingAttempts();
  }, [getInterviewRemainingAttempts]);

  const handleCheckboxChange = () => {
    setIsAcknowledged(prev => !prev);
    setErrorMessage('');
  };

  const handleConfirmation = () => {
    if (!isAcknowledged) {
      setErrorMessage('Please read and acknowledge the interview guidelines before proceeding.');
    } else {
      navigate(`/enrolledCourse/ai-interview/${id}/${courseName}/exam`);
    }
  };

  const applyPromo = async () => {
    try {
      setPromoError('');
      const { data } = await axios.post(`${JOBS_PORTAL_URL}/api/promocode/apply`, {
        code: promoCode,
        userId,
        originalPrice: originalAmount,
      });

      if (data?.finalPrice < originalAmount) {
        const discount = originalAmount - data.finalPrice;
        setDiscountAmount(discount);
        toast.success(`Promo applied! You saved ₹${discount}`);
      } else {
        setDiscountAmount(0);
        setPromoError("Invalid or expired promo code.");
      }
    } catch (err) {
      setPromoError(err?.response?.data || "Promo code failed.");
    }
  };

  const handlePayment = async () => {
    try {
      setButtonLoading(true);
      const amount = Math.round(totalAmount);
      const { data } = await axios.post(`${COURSES_URL}/payments/create-order`, { amount });

      const razorpay = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: amount * 100,
        currency: "INR",
        order_id: data.orderId,
        prefill: { name: fullName, email, contact: phoneNumber },
        theme: { color: "#0B89C2" },
        handler: async function (response) {
          const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = response;

          try {
            const enrollmentDTO = {
              courses: [{ courseId: id, courseName: "interview" }],
              skilltecheraUserId: userId,
              paymentId: razorpay_payment_id,
              orderId: razorpay_order_id,
              signature: razorpay_signature,
              totalAmount: amount,
              transactionDate: new Date().toISOString(),
              paymentType: "Razorpay",
              purchasePurpose: "AI_Interview_Payment",
              promoCode: promoCode || null,
            };

            await axios.post(`${COURSES_URL}/payments/verify-payment`, {
              razorpay_payment_id,
              razorpay_order_id,
              razorpay_signature,
            });

            await axios.post(`${COURSES_URL}/payments`, enrollmentDTO);
            await axios.post(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/handle-remaining-attempts/${userId}/${id}/${amount}`);

            toast.success("Payment successful! You can now start the interview.");
            getInterviewRemainingAttempts();
          } catch (err) {
            console.error("Error during post-payment processing:", err);
            toast.error("Something went wrong after payment.");
          }
        },
      });

      razorpay.open();
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error("Failed to initiate payment.");
    } finally {
      setButtonLoading(false);
      setLoading(false);
    }
  };

  // 🔒 Prevent premature UI display
  if (isLoadingInitialCheck) {
    return (
      <div className="loading-overlay">
        <ClipLoader color="#333" loading={true} size={40} />
      </div>
    );
  }

  return (
    <div className="ai-interview-landing-container">
      {loading && (
        <div className="loading-overlay">
          <ClipLoader color="#333" loading={true} size={40} />
        </div>
      )}

      {interviewRemainingAttempts ? (
        <div className="ai-interview-landing">
          <h2>AI Interview Instructions</h2>
          <ul>
            <li>Ensure a quiet, distraction-free environment.</li>
            <li>Stable internet, mic, and webcam are required.</li>
            <li>Do not open other tabs during the interview.</li>
            <li>The interview is recorded and confidential.</li>
            <li>Fraudulent behavior may result in termination.</li>
          </ul>
          <p>By continuing, you confirm that you have read and understood the guidelines.</p>
          <div>
            <input
              type="checkbox"
              id="acknowledge"
              checked={isAcknowledged}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="acknowledge" className="acknowledge-interview-label">
              I acknowledge that I have read and understood the interview guidelines.
            </label>
          </div>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          <button onClick={handleConfirmation} disabled={!isAcknowledged}>
            Confirm
          </button>
        </div>
      ) : (
        <div className="interview-payment-main-container">
          <div className="interview-payment-container">
            <h1 className="payment-header">Unlock More Attempts for Your AI Interview</h1>
            <p>Pay ₹{originalAmount} and unlock 3 more attempts.</p>

            <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
              <input
                type="text"
                placeholder="Enter Promo Code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                style={{
                  padding: '10px',
                  fontSize: '16px',
                  width: '250px',
                  marginRight: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                }}
              />
              <button
                onClick={applyPromo}
                style={{
                  padding: '10px 20px',
                  fontSize: '16px',
                  backgroundColor: '#0B89C2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                }}
              >
                Apply
              </button>
            </div>
            {promoError && <p style={{ color: 'red', marginTop: '10px' }}>{promoError}</p>}
            {discountAmount > 0 && (
              <p style={{ color: 'green', marginTop: '10px' }}>Discount: ₹{discountAmount}</p>
            )}

            <button onClick={handlePayment} className="pay-now-button">
              {buttonLoading ? 'Processing...' : `Pay ₹${totalAmount}`}
            </button>
          </div>
          <img src={cardPayment} alt="payment" className="interview-payment-img" />
        </div>
      )}
    </div>
  );
};

export default AIInterviewLanding;
